import { clone, filter, find, forEach, map, sumBy } from "lodash";

import itemTypeIds from "../../constants/itemTypeIds";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import { typeSelectors } from "../../modules/definition/types";
import arrayToIdReferenceList from "./arrayToIdReferenceList";
import { findExistingReferenceForVersion2Id, findExistingTypeForVersion2Id } from "./mapOldToNewTypes";
import upgradeCalendar from "./upgradeCalendar";
import upgradeColor from "./upgradeColor";
import upgradeProperty from "./upgradeProperty";
import upgradeReference from "./upgradeReference";
import upgradeType from "./upgradeType";
import zoomLimitTextToValue from "./zoomLimitTextToValue";

const ALWAYS_ENABLED_TYPES = [itemTypeIds.MINDMAP_NODE_TYPE_ID];
const NARRATIVE_ENABLED_TYPES = [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID];

const getEventProperties = v2Properties => {
  // We will only use properties brought in from the old file... no point having any existing ones
  let properties = map(v2Properties, upgradeProperty);

  // See how many multi-text properties there are
  const multitextCount = sumBy(properties, p => Number(p.format === PropertyFormatIdentifier.multitext));
  // If there is only one, or there is one called "notes" or "summary", use it for summary
  const eventSummaryProperty = find(
    properties,
    p =>
      p.format === PropertyFormatIdentifier.multitext &&
      (multitextCount === 1 || p.label === "Notes" || p.label === "Summary")
  );
  // and remove it from properties
  properties = eventSummaryProperty ? filter(properties, p => p.id !== eventSummaryProperty.id) : properties;
  return { properties, eventSummaryProperty };
};

// Merge the properties with same key and format
const mergeProperties = (v3Properties, v2EventProperties) => {
  let newPropIdForV2Id = {};
  const { byId, allIds } = v3Properties;
  const v3PropsArray = Object.values(byId);
  v2EventProperties.forEach(v2P => {
    const foundV3Prop = v3PropsArray.find(v3P => {
      // Found the matching v3 property
      if (v3P.key === v2P.key && v3P.format === v2P.format) {
        newPropIdForV2Id[v2P.id] = v3P.id;
        return true;
      }
      return false;
    });
    if (!foundV3Prop) {
      // Not found, add to the v3 properties
      byId[v2P.id] = v2P;
      allIds.push(v2P.id);
    }
  });
  return { properties: v3Properties, newPropIdForV2Id };
};

export default (v2Template, v3DefinitionsRecord, enabledOtherTypeIds = []) => {
  const v2Types = v2Template.types;

  const v2Properties = v2Template.properties;
  const v2RangeProperty = v2Template.rangeProperties[0];

  // First, convert the selected new v3 template definitions into a definitions record
  let definitions = v3DefinitionsRecord.toJS();

  // Replace the calendar with their version 2 file
  const { calendar, updateDatesInBC } = upgradeCalendar(v2RangeProperty.calendar);
  definitions.calendar = calendar;
  // Colors can be dropped straight in
  const colors = map(v2Template.colors, upgradeColor);
  colors.sort((a, b) => a.sortOrder > b.sortOrder);
  definitions.colors = arrayToIdReferenceList(colors);

  let { properties: v2EventProperties, eventSummaryProperty } = getEventProperties(v2Properties);

  const { properties, newPropIdForV2Id } = mergeProperties(definitions.properties, v2EventProperties);

  definitions.properties = properties;

  let enabledTypeIds = [...ALWAYS_ENABLED_TYPES, ...enabledOtherTypeIds];

  if (definitions.includeNarrative) {
    enabledTypeIds.push(...NARRATIVE_ENABLED_TYPES);
  }

  let newTypesToAddToRelatesTo = [];
  let newTypeIdForV2Id = {};
  let newReferenceIdForV2Id = {};
  let newReferencesToAddToEventTypes = [];

  const sortedV2Types = clone(v2Types).sort((a, b) => a.sortOrder > b.sortOrder);

  // Keep track of the existing type IDs so that we can sort them into the right order
  let existingTypeIds = [itemTypeIds.EVENT_ITEM_TYPE_ID];

  // For all of the old version 2 types:
  // If we can match it to a type in the new definitions, then update its values
  // Otherwise, add it into the definitions
  forEach(sortedV2Types, v2Type => {
    const matchingTypeId = findExistingTypeForVersion2Id(definitions.types.allIds, v2Type.guid, v2Type.name);
    const matchingType = matchingTypeId ? definitions.types.byId[matchingTypeId] : null;
    const upgradedType = upgradeType(matchingType, v2Type);
    definitions.types.byId[upgradedType.id] = upgradedType;
    if (!matchingTypeId) {
      definitions.types.allIds.push(upgradedType.id);
      newTypesToAddToRelatesTo.push(upgradedType.id);
    }
    // And store which type ID we are using for later updating of entities
    newTypeIdForV2Id[v2Type.guid] = upgradedType.id;
    enabledTypeIds.push(upgradedType.id);
    existingTypeIds.push(upgradedType.id);
  });

  let existingReferenceIds = [];

  // For all of the old version 2 roles:
  // If we can match it to a type in the new definitions, then update its values
  // Otherwise, add it into the definitions
  forEach(sortedV2Types, v2Type => {
    const typeId = newTypeIdForV2Id[v2Type.guid];
    const sortedRoles = clone(v2Type.roles).sort((a, b) => a.sortOrder > b.sortOrder);

    forEach(sortedRoles, v2Role => {
      const matchingRefId = findExistingReferenceForVersion2Id(definitions.references.allIds, v2Role.guid, v2Role.name);
      const matchingRef = matchingRefId ? definitions.references.byId[matchingRefId] : null;
      const upgradedRef = upgradeReference(matchingRef, v2Role, typeId);
      definitions.references.byId[upgradedRef.id] = upgradedRef;
      if (!matchingRefId) {
        definitions.references.allIds.push(upgradedRef.id);
        // We will also need to add this new reference into all of our event types later
        newReferencesToAddToEventTypes.push(upgradedRef.id);
      }
      // And store which role ID we are using for later updating of entities
      newReferenceIdForV2Id[v2Role.guid] = upgradedRef.id;
      existingReferenceIds.push(upgradedRef.id);
    });
  });

  let defaultEventTypeId = null;

  // Now we need to update the event types to add the appropriate references and properties
  // We also want to make a list of types to delete from the definition, if they are not being used
  // by the imported data...
  forEach(definitions.types.allIds, typeId => {
    const type = definitions.types.byId[typeId];
    if (typeSelectors.isEventType(type)) {
      type.properties = definitions.properties.allIds;
      type.references = [...type.references, ...newReferencesToAddToEventTypes];
      if (!defaultEventTypeId && type.enabled) {
        defaultEventTypeId = type.id;
      }
    } else {
      type.enabled = enabledTypeIds.includes(type.id);
    }
  });

  // Sort the Types so that the pre-existing ones appear first, and the rest are sorted
  // according to their usual template order
  const v3TypeOrder = clone(definitions.types.allIds);

  definitions.types.allIds.sort((a, b) => {
    const v2A = existingTypeIds.indexOf(a);
    const v2B = existingTypeIds.indexOf(b);
    if (v2A >= 0 && v2B >= 0) {
      return v2A - v2B;
    } else if (v2A >= 0) {
      return -1;
    } else if (v2B >= 0) {
      return 1;
    }
    const v3A = v3TypeOrder.indexOf(a);
    const v3B = v3TypeOrder.indexOf(b);
    return v3A - v3B;
  });

  // Add the newly created types to "relates to"...
  definitions.references.byId[REFERENCE_RELATES_TO].types.push(...newTypesToAddToRelatesTo);

  // Sort the References so that the pre-existing ones appear first, and the rest are sorted
  // according to their usual template order
  const v3RefOrder = clone(definitions.references.allIds);

  definitions.references.allIds.sort((a, b) => {
    const v2A = existingReferenceIds.indexOf(a);
    const v2B = existingReferenceIds.indexOf(b);
    if (v2A >= 0 && v2B >= 0) {
      return v2A - v2B;
    } else if (v2A >= 0) {
      return -1;
    } else if (v2B >= 0) {
      return 1;
    }
    const v3A = v3RefOrder.indexOf(a);
    const v3B = v3RefOrder.indexOf(b);
    return v3A - v3B;
  });

  // Update timelineViewSettings
  definitions.timelineViewSettings.zoomTo = zoomLimitTextToValue(v2RangeProperty.minUnitScale);
  definitions.timelineViewSettings.zoomFrom = zoomLimitTextToValue(v2RangeProperty.maxUnitScale);

  // TODO: Are there any other view settings we should set?

  return {
    definitions,
    eventSummaryProperty,
    newPropIdForV2Id,
    newTypeIdForV2Id,
    newReferenceIdForV2Id,
    defaultEventTypeId,
    updateDatesInBC
  };
};
